import MuiAlert, { AlertProps } from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import React, {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useState,
} from "react";

interface ToastContextType {
	showToast: (
		message: string | JSX.Element,
		severity: "success" | "error" | "warning" | "info"
	) => void;
}

type ToastItem = {
  id: number;
  message: string | JSX.Element;
  severity: "success" | "error" | "warning" | "info";
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
	props,
	ref
) {
	return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const showToast = useCallback(
    (
      message: string | JSX.Element,
      severity: "success" | "error" | "warning" | "info"
    ) => {
      setToasts((prev) => [...prev, { id: Date.now() + Math.random(), message, severity }]);
    },
    []
  );

	const handleClose = (id: number, reason?: string) => {
		if (reason === "clickaway") return;
		setToasts((prev) => prev.filter((t) => t.id !== id));
	};

	return (
		<ToastContext.Provider value={{ showToast }}>
			{children}
			{toasts.map((toast, idx) => (
				<Snackbar
					key={toast.id}
					anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
					open
					autoHideDuration={3000}
					onClose={(_, reason) => handleClose(toast.id, reason)}
                    style={{ marginBottom: idx * 68 + 12 }}
				>
					<Alert
						onClose={() => handleClose(toast.id)}
						severity={toast.severity}
						sx={{ width: "100%" }}
					>
						{toast.message}
					</Alert>
				</Snackbar>
			))}
		</ToastContext.Provider>
	);
};

export const useToast = () => {
	const context = useContext(ToastContext);
	if (!context) {
		throw new Error("useToast must be used within a ToastProvider");
	}
	return context;
};
